/**
 * Dataset API Service
 *
 * API functions for managing datasets in the AI module.
 * Connects to the external AI backend service.
 */

import config from "../config";
import type {
  DatasetCreateRequest,
  DatasetResponse,
  DatasetLabelsListResponse,
  DatasetLabelsUpsertRequest,
  DatasetLabelsUpsertResponse,
} from "../types/dataset";

const API_URL = `${config.backendUrl}/datasets`;

async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      console.error("API Error:", {
        status: response.status,
        url: url,
        body: errorData,
      });

      if (errorData.detail) {
        errorMessage =
          typeof errorData.detail === "string"
            ? errorData.detail
            : JSON.stringify(errorData.detail);
      } else if (errorData.error) {
        errorMessage =
          typeof errorData.error === "string"
            ? errorData.error
            : errorData.error.message || JSON.stringify(errorData.error);
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      } else {
        errorMessage = JSON.stringify(errorData);
      }
    } catch {
      errorMessage = response.statusText;
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

export const getDatasets = async (): Promise<DatasetResponse[]> => {
  return apiFetch<DatasetResponse[]>(API_URL);
};

export const createDataset = async (
  dataset: DatasetCreateRequest
): Promise<DatasetResponse> => {
  console.log("Creating dataset with data:", JSON.stringify(dataset, null, 2));

  return apiFetch<DatasetResponse>(API_URL, {
    method: "POST",
    body: JSON.stringify(dataset),
  });
};

export const deleteDataset = async (id: string): Promise<void> => {
  await apiFetch<null>(`${API_URL}/${id}`, { method: "DELETE" });
};

export const getDatasetById = async (id: string): Promise<DatasetResponse> => {
  return apiFetch<DatasetResponse>(`${API_URL}/${id}`);
};

export const getDatasetLabels = async (
  id: string
): Promise<DatasetLabelsListResponse> => {
  return apiFetch<DatasetLabelsListResponse>(`${API_URL}/${id}/labels`);
};

export const upsertDatasetLabels = async (
  id: string,
  data: DatasetLabelsUpsertRequest
): Promise<DatasetLabelsUpsertResponse> => {
  return apiFetch<DatasetLabelsUpsertResponse>(`${API_URL}/${id}/labels`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};
